import {
    BAGGAGE_PRICE,
    CONV_FEE,
    COUPON_DISCOUNT_WITH_VAT,
    FARE_PRICE,
    GV_DISCOUNT,
    INSURANCE_PRICE_WITH_VAT,
    MEAL_PRICE,
    SEAT_PRICE
} from '@/src/constants/priceSummary'
import {
    useAncillaryPricing,
    useDiscountCalculation,
    useFarePricingValues,
    useFareSelection,
    useInsurancePricing,
    useItineraryData,
    usePricingItems,
    useVatCalculation,
    type PricingItem,
    type PricingSubItem
} from './pricing'

// Re-export types for backward compatibility
export type { PricingItem, PricingSubItem }

/**
 * Refactored hook that orchestrates multiple smaller pricing hooks
 * Maintains the same API for backward compatibility
 */
const usePriceCalculation = () => {
    // Fetch all itinerary data
    const {
        faresList,
        fareFamilyEnabledOnReview,
        fareFamilyFares,
        selectedInsurances,
        vasAncillaries,
        paxPricingInfoJson,
        passengers,
        couponDetails,
        giftVoucherData,
        selectedAncillaries,
        meta
    } = useItineraryData()

    // Calculate ancillary prices (baggage, meals, seats)
    const { baggagePrice, mealPrice, seatPrice } = useAncillaryPricing(selectedAncillaries)

    // Determine selected fare and fare family
    const { selectedFare, selectedFareFamily } = useFareSelection(faresList, fareFamilyFares)

    // Calculate insurance pricing
    const { insurancePricingConfig, totalInsurancePrice, totalInsurancePriceWithVat } =
        useInsurancePricing(selectedInsurances)

    // Calculate VAT adjustments from VAS services and coupons
    // Note: These will only be applied if meta.vatApplicable is true
    const { additionalVat } = useVatCalculation(
        vasAncillaries,
        selectedInsurances,
        couponDetails,
        selectedFare,
        selectedFareFamily,
        fareFamilyEnabledOnReview
    )

    // Extract fare pricing values
    const {
        farePrice,
        basePrice,
        taxes,
        totalInstantDiscountForFareFamilyFares,
        convenienceFee,
        totalPremiumAmount,
        totalPremiumAmountWithVat,
        faresVat,
        fareInsuranceTitle,
        fareInsuranceTitleWithVat
    } = useFarePricingValues(selectedFare, selectedFareFamily, fareFamilyEnabledOnReview)

    // Determine if VAT adjustments should be applied
    const shouldApplyVatAdjustments = !!meta?.vatApplicable

    // Calculate discount values
    const { totalCouponAmount, totalCouponAmountWithVat, couponCode, totalGvAmount, totalInstantDiscountForRegularFares } =
        useDiscountCalculation(couponDetails, paxPricingInfoJson, fareFamilyEnabledOnReview, giftVoucherData)

    // Generate pricing items for display
    const { pricingItems, pricingConfig, getEffectivePricing } = usePricingItems({
        farePrice,
        basePrice,
        taxes,
        convenienceFee,
        fareInsuranceTitle,
        fareInsuranceTitleWithVat,
        totalPremiumAmount,
        totalPremiumAmountWithVat,
        totalInsurancePrice,
        totalInsurancePriceWithVat,
        seatPrice,
        mealPrice,
        baggagePrice,
        totalCouponAmount,
        totalCouponAmountWithVat,
        totalGvAmount,
        totalInstantDiscount: fareFamilyEnabledOnReview
            ? totalInstantDiscountForFareFamilyFares
            : totalInstantDiscountForRegularFares,
        couponCode,
        insurancePricingConfig,
        fareFamilyEnabledOnReview,
        paxPricingInfoJson,
        passengers,
        faresVat,
        additionalVat: shouldApplyVatAdjustments ? additionalVat : 0
    })

    // Calculate total price using the same pricing elements as before
    const totalPrice = getEffectivePricing([
        CONV_FEE,
        FARE_PRICE,
        SEAT_PRICE,
        MEAL_PRICE,
        BAGGAGE_PRICE,
        INSURANCE_PRICE_WITH_VAT,
        GV_DISCOUNT,
        COUPON_DISCOUNT_WITH_VAT,
        fareInsuranceTitleWithVat
    ])

    const totalTravellers = passengers?.length || 0

    return { totalPrice, pricingItems, pricingConfig, totalTravellers }
}

export default usePriceCalculation
